/**
 * The drop zone's four states, in one place (DIV-012).
 *
 * The zone and the filled row are both rendered by `buildImageElement`; this only
 * swaps which is visible and fills the row in. Keeping the transitions here rather
 * than inline in the upload handler means the failure path and the remove path
 * cannot drift apart from the success path — the bug that would show a shopper a
 * filename for a file that is no longer attached.
 *
 * The status lives in the row, so nothing fades: a mark beside the name and the
 * state in the sub-line. The separate `.cpc-upload-status` span is left alone,
 * because the BUTTON style still uses it and its 3-second fade is pinned by
 * ELEM-IMGUP-004.
 */
import { t } from "./base";
import { formatFileSize, isImageFile } from "./file-types";
import $ from "jquery";

/** Registered in the API's AppPhrases so each reaches the translation table. */
export const UPLOADING_STATE = "Uploading…";
export const UPLOADED_STATE = "Uploaded";
export const UPLOAD_FAILED_STATE = "Upload failed — please try again";

const TICK =
  "<svg width='14' height='14' viewBox='0 0 16 16' aria-hidden='true'><circle cx='8' cy='8' r='7' fill='#28a745'></circle><path d='M5 8 L7 10 L11 6' stroke='#fff' stroke-width='2' fill='none' stroke-linecap='round' stroke-linejoin='round'></path></svg>";
const CROSS =
  "<svg width='14' height='14' viewBox='0 0 16 16' aria-hidden='true'><circle cx='8' cy='8' r='7' fill='#d72c0d'></circle><path d='M5 5 L11 11 M11 5 L5 11' stroke='#fff' stroke-width='2' stroke-linecap='round'></path></svg>";

function parts(propertyName: any) {
  const escaped = `[data-property-name="${propertyName}"]`;
  const row = $(`.cpc-drop-zone__file${escaped}`);
  return {
    row,
    zone: row.siblings(".cpc-drop-zone").add(row.parent().find(".cpc-drop-zone")),
    thumb: row.find(".cpc-drop-zone__thumb"),
    name: row.find(".cpc-drop-zone__name"),
    state: row.find(".cpc-drop-zone__state"),
    mark: row.find(".cpc-drop-zone__mark"),
    remove: row.find(".cpc-drop-zone__remove"),
  };
}

/** A file has been accepted and is on its way up. */
export function showUploading(propertyName: any, fileName: string): void {
  const p = parts(propertyName);
  if (!p.row.length) return;
  p.zone.hide();
  p.row.css("display", "flex");
  p.thumb.text(extensionChip(fileName));
  p.name.text(fileName);
  p.mark.empty();
  p.state.text(t(UPLOADING_STATE)).css("color", "");
  // Removing mid-flight would leave an upload running against a cleared input.
  p.remove.hide();
}

/** The upload finished; swap in the thumbnail and settle the sub-line. */
export function showUploaded(
  propertyName: any,
  fileName: string,
  sizeBytes: number,
  fileUrl: string,
): void {
  const p = parts(propertyName);
  if (!p.row.length) return;
  const size = formatFileSize(sizeBytes);
  const kind = extensionChip(fileName);

  if (isImageFile(fileName)) {
    // A broken thumbnail is worse than none: the CDN URL can 404 for a moment while
    // S3 settles. The handler is attached rather than inlined so the fallback is the
    // extension chip — the same thing a PDF shows — instead of an empty square.
    const img = new Image();
    img.alt = "";
    img.setAttribute("style", "width:100%; height:100%; object-fit:cover;");
    img.onerror = () => {
      p.thumb.empty().text(kind);
    };
    img.src = fileUrl;
    p.thumb.empty().append(img);
  } else {
    p.thumb.text(kind);
  }

  p.name.text(fileName);
  p.mark.html(TICK);
  p.state.text(size ? `${kind} · ${size}` : t(UPLOADED_STATE)).css("color", "");
  p.remove.show();
}

/** Every retry is spent. The row stays, so the shopper can see what failed. */
export function showFailed(propertyName: any, fileName: string): void {
  const p = parts(propertyName);
  if (!p.row.length) return;
  p.thumb.text(extensionChip(fileName));
  p.name.text(fileName);
  p.mark.html(CROSS);
  p.state.text(t(UPLOAD_FAILED_STATE)).css("color", "#d72c0d");
  p.remove.show();
}

/** Back to an empty zone — the shopper removed the file, or it was refused. */
export function showEmpty(propertyName: any): void {
  const p = parts(propertyName);
  if (!p.row.length) return;
  p.row.hide();
  p.zone.show();
  p.thumb.empty().text("");
  p.name.text("");
  p.mark.empty();
  p.state.text("").css("color", "");
}

function extensionChip(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  return dot > 0 ? fileName.slice(dot + 1).toUpperCase() : "FILE";
}

function escapeHtml(value: string): string {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
